import { useNavigate, useParams } from 'react-router-dom'

import {
  DuplicateClassModalDocument,
  DuplicateTrainingClassDocument,
  GroupsTableDocument,
  TrainingClassesDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { FormErrors, FormProvider } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { FORMAT, dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DuplicateClassModal } from 'modules/trainings/components/Revamp/TrainingsDetail/Classes/DuplicateClass'

import { DuplicateClassForm } from './types'

const DuplicateClass = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { id, classId } = useParams()
  const { data } = useQuery(DuplicateClassModalDocument, {
    variables: {
      classId: String(classId),
    },
  })
  const [duplicateClass, { loading: loadingDuplicate }] = useMutation(
    DuplicateTrainingClassDocument,
    {
      extractorFn: (data) => data.trainingsMutations.duplicateTrainingsTrainingClass,
      awaitRefetchQueries: true,
      refetchQueries: [TrainingClassesDocument, GroupsTableDocument],
    }
  )

  const defaultValues = {
    name: `${data?.trainings.trainingClass?.name} (Copy)`,
    startDate: undefined,
    endDate: undefined,
    assignedParticipants: false,
    costs: false,
    materials: false,
    sessions: true,
  }

  if (id) {
    const onCloseRoute = navigation.trainings.list.details_revamp.view.trainingClasses.tab.fullPath(
      {
        id,
      }
    )
    return (
      <FormProvider
        defaultValues={defaultValues}
        validateOnMount
        validate={({ endDate, startDate, name }) => {
          const errors: FormErrors<DuplicateClassForm> = {}
          if (!name) {
            errors.name = i18n.t(
              'trainings.trainings_revamp.classes.duplicate_class_modal.params.mandatory'
            )
          }
          if (!startDate) {
            errors.startDate = i18n.t(
              'trainings.trainings_revamp.classes.duplicate_class_modal.params.mandatory'
            )
          }
          if (!endDate) {
            errors.endDate = i18n.t(
              'trainings.trainings_revamp.classes.duplicate_class_modal.params.mandatory'
            )
          }
          if (moment(endDate, FORMAT.ui).isBefore(moment(startDate, FORMAT.ui))) {
            errors.endDate = i18n.t(
              'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.due_date_error'
            )
          }
          return errors
        }}
        onSubmit={async (values) => {
          if (values.name && values.startDate && values.endDate) {
            const result = await duplicateClass({
              variables: {
                id: String(classId),
                name: values.name,
                startDate: dateUIToBackend(values.startDate),
                endDate: dateUIToBackend(values.endDate),
                employees: values.assignedParticipants,
                costs: values.costs,
                classFiles: values.materials,
                sessions: values.sessions,
              },
            })
            const handledResult = handleResult(result, () => {
              app.setFlash({
                type: 'wrong',
                message: i18n.t('trainings.trainings_revamp.classes.duplicate_class_modal.error'),
              })
            })
            if (handledResult.success) {
              app.setFlash({
                type: 'correct',
                message: i18n.t('trainings.trainings_revamp.classes.duplicate_class_modal.success'),
              })
              navigate(onCloseRoute)
            }
            return handledResult
          }
          throw 'Error'
        }}
      >
        <DuplicateClassModal onCloseRoute={onCloseRoute} loading={loadingDuplicate} />
      </FormProvider>
    )
  }
}

export default DuplicateClass
